import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Popover, PopoverBody, PopoverContent, PopoverTrigger, rem, useDisclosure, useOutsideClick, VStack } from '@bonusly/design-system';
import { useAnalytics } from '@bonusly/who-am-i';

import { EventName } from '../../lib/analytics';
import { NotificationStatus } from '../../lib/notification/notification';
import { UserProfile } from '../../types/user';
import { NotificationEmptyState, NotificationHeader, NotificationIcon, NotificationItem } from '../Notification';
import { useFetchNotifications } from '../Notification/hooks/useFetchNotifications';
import { useInvalidateNotifications } from '../Notification/hooks/useInvalidateNotifications';
import { NotificationUpdateType, useUpdateNotifications } from '../Notification/hooks/useUpdateNotifications';
import { NAVBAR_HEIGHT_IN_PX } from './config';
import { NavGroup } from './NavGroup';

export interface NavMenuProps {
	user: UserProfile;
}

export const NavNotificationMenu: FC<NavMenuProps> = ({ user }) => {
	const invalidate = useInvalidateNotifications();
	const { notifications, unreadCount } = useFetchNotifications(user.locale);

	const { track } = useAnalytics();
	const handleUpdateNotification = useUpdateNotifications('mark-all-read', {});
	const { t } = useTranslation('navigation');

	const { isOpen, onClose, onOpen } = useDisclosure();
	const popoverRef = useRef<HTMLDivElement>(null);

	const refOutsideClick = useRef(null);

	useOutsideClick({
		ref: refOutsideClick,
		handler: onClose,
	});

	const handleClick = async () => {
		if (isOpen) {
			return onClose();
		}

		onOpen();

		track(EventName.NotificationsViewList);

		const unReadNotifications = notifications.filter((notification) => notification.status === NotificationStatus.Unread);

		// TODO: These should be updated in a single request
		if (unReadNotifications.length > 0) {
			const updateNotifications = unReadNotifications.map((notification) =>
				handleUpdateNotification.mutateAsync({
					notificationId: notification.id,
					type: NotificationUpdateType.MarkAsSeen,
				})
			);

			await Promise.all(updateNotifications);

			invalidate();
		}
	};

	const handleMarkAllRead = async () => {
		onClose();

		const unReadNotifications =
			notifications
				?.filter((notification) => notification.status !== NotificationStatus.Read)
				.map((notification) =>
					handleUpdateNotification.mutateAsync({
						notificationId: notification.id,
						type: NotificationUpdateType.MarkAsRead,
					})
				) ?? [];

		await Promise.all(unReadNotifications);

		invalidate();
		track(EventName.NotificationsMarkAllRead);
	};

	return (
		<Box ref={refOutsideClick}>
			<Popover placement="bottom-end" onClose={onClose} isOpen={isOpen} initialFocusRef={popoverRef} offset={[56, 8]}>
				<PopoverTrigger>
					<NotificationIcon handleClick={handleClick} unreadCount={unreadCount} />
				</PopoverTrigger>

				<PopoverContent
					borderColor="gray.50"
					w={rem(450)}
					maxH={`min(calc(${rem(window.innerHeight)} - ${rem(NAVBAR_HEIGHT_IN_PX)} - ${rem(20)}), ${rem(922)})`}
					overflowY="auto"
					overflowX="hidden"
					aria-label={t('notification.list')}
				>
					<PopoverBody as="div" p={0}>
						<NavGroup p={rem(8)}>
							<VStack gap={0}>
								<NotificationHeader handleReadAll={handleMarkAllRead} />
								<VStack gap={0} as="ul" role="listbox" aria-label={t('notification.menu_label')} w="100%">
									{notifications.length > 0 ? (
										notifications.map((notification) => <NotificationItem notification={notification} key={notification.id} handleClose={onClose} />)
									) : (
										<NotificationEmptyState />
									)}
								</VStack>
							</VStack>
						</NavGroup>
					</PopoverBody>
				</PopoverContent>
			</Popover>
		</Box>
	);
};
